import CancelIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Fab,
  Grid,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import RowElement from "components/RowElement";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import { makeStyles } from "helpers/StyleHelper";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Navigate, useParams } from "react-router-dom";
import {
  ISaveSettingValueRequest,
  ResponseOfSettingCache,
  SaveResponse,
  SaveSettingValueRequest,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import {
  SettingGroupId,
  Severity,
  SnackbarMessageProps,
} from "types/common-types";
import translateError from "utils/translateError";
import ApplicationOverridesTable from "./ApplicationOverridesTable";
import DescendantSettingsTable from "./DescendantSettingsTable";
import SettingValueForm from "./SettingValueForm";

const useStyles = makeStyles(() => ({
  rowContent: {
    float: "right",
  },
  rowTitle: {
    float: "left",
    fontWeight: 600,
  },
}));

const SettingDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { settingService, settingsUrl } = useGlobalContext();
  const { formatMessage: t } = useIntl();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const { registerPromise } = useTrashablePromises();
  const styles = useStyles(useTheme());
  const [readonly, setReadonly] = useState(true);
  const toggleReadonly = () => setReadonly(!readonly);
  const queryClient = useQueryClient();
  const { organizationId } = contextStore.useState();

  useEffect(() => {
    setReadonly(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const settingResult = useQuery<ResponseOfSettingCache>(
    ["settingById", id, organizationId],
    () => settingService.getSettingById(id!),
    {
      enabled: !!id,
    }
  );

  const setting = settingResult.data?.data;
  useTitle(setting?.name || "Setting Details");

  if (!id) return <Navigate to={settingsUrl()} replace />;
  if (settingResult.isLoading) return <Loader />;
  if (settingResult.isError) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="settingLoadError" />
        </Alert>
      </Box>
    );
  }

  const detailsRows = [
    RowElement("nameLabel", setting?.name!),
    RowElement("typeLabel", setting?.typeName!),
    RowElement("descriptionLabel", setting?.description!),
    RowElement("groupLabel", setting?.groupName!),
    RowElement("codeConstantLabel", setting?.codeConstant!),
  ].filter((r) => !!r.component);

  const onSubmit = (values: ISaveSettingValueRequest) => {
    return registerPromise(
      settingService.saveSettingValue(new SaveSettingValueRequest(values))
    )
      .then(({ id }: SaveResponse) => {
        queryClient.invalidateQueries("settingById");
        queryClient.invalidateQueries("settings");
        queryClient.invalidateQueries("getEmailTheme");
        queryClient.invalidateQueries("settingGroups");
        queryClient.invalidateQueries("evaluationDefaultSettings");
        queryClient.invalidateQueries("settingService.getApplicationOverrides");
        setSnackbarMessage(
          new SnackbarMessageProps(t({ id: "settingValueSaved" }))
        );
        toggleReadonly();
      })
      .catch((err: any) => {
        console.log(err);
        setSnackbarMessage(
          new SnackbarMessageProps(translateError(err, t), Severity.Error)
        );
        return { [FORM_ERROR]: translateError(err, t) };
      });
  };

  const onDelete = () => {
    return registerPromise(
      settingService.deleteSettingValue(setting?.settingValueId!)
    )
      .then(() => {
        queryClient.invalidateQueries("settingById");
        queryClient.invalidateQueries("settings");
        queryClient.invalidateQueries("getEmailTheme");
        queryClient.invalidateQueries("settingGroups");
        queryClient.invalidateQueries("evaluationDefaultSettings");
        queryClient.invalidateQueries("settingService.getApplicationOverrides");
        setSnackbarMessage(
          new SnackbarMessageProps(t({ id: "settingValueDeleted" }))
        );
        toggleReadonly();
      })
      .catch((err: any) => {
        return { [FORM_ERROR]: translateError(err, t) };
      });
  };

  return (
    <div>
      <Box my={2}>
        <RedirectBackButton
          redirectRoute={settingsUrl()}
          labelKey="backToDetailsButtonLabel"
        />
      </Box>

      <h1>{setting?.name}</h1>

      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {detailsRows.map(({ title, component }, i) => (
              <TableRow key={i}>
                <TableCell component="th" scope="row" style={{ width: "20vw" }}>
                  <Box component="span" sx={styles.rowTitle}>
                    {title}
                  </Box>
                </TableCell>
                <TableCell>{component}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box my={1} display="flex" justifyContent="start">
        <h2>
          <T id="settingValueLabel" />
        </h2>
        <Box ml={4} />
        <Link my={1} style={{ textDecoration: "none" }}>
          <Fab color="primary" data-cy="editButton" onClick={toggleReadonly}>
            {readonly ? <EditIcon /> : <CancelIcon />}
          </Fab>
        </Link>
      </Box>

      <SettingValueForm
        setting={setting!}
        readonly={readonly}
        onSubmit={onSubmit}
        onDelete={onDelete}
      ></SettingValueForm>

      <Box mb={3} />

      <Grid container direction="column" spacing={2}>
        {setting?.settingGroupId !== SettingGroupId.Features && (
          <Grid item>
            <Accordion key="applicationOverrides">
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>
                  <T id="applicationOverrides" />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ApplicationOverridesTable setting={setting!} />
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}
        <Grid item>
          <Accordion key="organizationChildren">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>
                <T id="childOrganizations" />
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <DescendantSettingsTable setting={setting!} />
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </div>
  );
};

export default SettingDetails;
